import type { ThemePreference } from 'helpers/theme'
import { IFilterType, sortDirections, IColumnTypes } from 'components/spreadsheet/types'
import {
  AccessResourceKinds,
  AccessAttributeKinds,
  IntegrationSyncResourceTypes,
  OrganisationRoles,
  ProjectRoles
} from 'app-constants'
import { ReactNode } from 'react'
import { RawDraftContentState } from 'draft-js'
import { ApiErrorCodes, ApiErrorLink } from 'helpers/api'

export interface ResponseMetadata {
  errors?: Array<any>
  size?: number
  total?: number
  totalPages?: number
  page?: number
  perPage?: number
  nextPageToken?: string
  require2FaRedirect?: boolean
  userToken?: string
  redirect?: string
  requireSSORedirect?: boolean
  hubCreationRestricted?: boolean
  ssoRedirectURL?: string
}

export interface ResponsePayload<T> {
  metadata: ResponseMetadata
  data: T
}

export interface ErrorContent {
  attribute: string | { tableId?: string; tableViewId?: string }
  messages: string[] | { [key: string]: string[] }
}

export interface ResponseError {
  code: ApiErrorCodes
  detail: {
    errors?: Array<ErrorContent>
    message: string
  }
  links?: ApiErrorLink[]
  /** The API's flag for an error only Morta can diagnose, so the app offers AI Support. */
  needsSupport?: boolean
}

export type ResourcePublicId = string // XXX: use this for the public id

export type AllowedDateFormats = 'DD/MM/YYYY' | 'DD-MM-YYYY' | 'MM/DD/YYYY' | 'MM-DD-YYYY' | 'YYYY/MM/DD' | 'YYYY-MM-DD'

export type AllowedDatetimeFormats =
  | 'DD/MM/YYYY HH:mm'
  | 'DD/MM/YYYY HH:mm:ss'
  | 'MM/DD/YYYY HH:mm'
  | 'MM/DD/YYYY HH:mm:ss'
  | 'YYYY-MM-DD HH:mm'
  | 'YYYY-MM-DDTHH:mm:ss'
  | 'YYYY-MM-DDTHH:mm'
  | 'YYYY-MM-DD HH:mm:ss'

export interface INotification {
  publicId: string
  triggers: ITrigger[]
  actions: IAction[]
  description: string
  processes: Array<Pick<IProcess, 'name' | 'publicId'>>
  tables: Array<Pick<ITable, 'publicId' | 'name' | 'defaultViewId'>>
}

export interface ITrigger {
  resource: string
  verb: string
}

export interface IActionHeader {
  key: string
  value: string
}

export interface IAction {
  publicId: string
  kind: string
  webhookUrl: string
  customHeaders: IActionHeader[]
}

export interface SelectDependencyColumn {
  columnId: string
  columnJoinId: string
}
export interface SelectTableOptions {
  cachedOptions?: string[] | { [key: string]: string[] }
  tableId: string
  viewId: string
  columnId: string
  dependencies: SelectDependencyColumn[]
  liveValues: boolean
}

export interface SelectKindOptions {
  publicId: string
  manualOptions: string[]
  tableOptions: SelectTableOptions
  autopopulate: boolean
  expandOptionsViaCellEntry?: boolean
}

export interface ITableColumn {
  publicId: string
  name: string
  isIndexed: boolean
  width: number
  kind: IColumnTypes
  kindOptions?: SelectKindOptions
  description: EditorContent | null
  script: string | null
  scriptEnabled: boolean
  formula: string | null
  formulaEnabled: boolean
  aggregate: number
  isJoined: boolean
  thousandSeparator: boolean
  displayLink: boolean
  decimalPlaces: number
  dateFormat: string | null
  defaultValue: string | null
  headerBackgroundColor: string
  headerTextColor: string
  exportWidth: number | null
  isSynced: boolean
}

export interface ITableViewColumn extends ITableColumn {
  sortOrder: number
  required: boolean
  stringValidation: string | null
  validationMessage: string | null
  hardValidation: boolean
  validationNoBlanks: boolean
  validationNoDuplicates: boolean
  displayValidationError: boolean
  blockInvalidSelectValues: boolean
  formWidth: number | null
}

export interface INewViewColumn {
  columnName: string
  columnId: string
  required: boolean
}

export interface INewView extends Partial<ITableView> {
  columns?: INewViewColumn[] | ITableViewColumn[]
  configurationParentId?: string | null
  context?: { projectId: string }
}

export interface ICreateTableJoin {
  joinTableId?: string
  joinViewId: string
  joinColumns: IMapJoinColumn[]
  dataColumns: string[]
  isOneToMany: boolean
}

export interface ITableViewCell extends Omit<IContextProperties, 'projectId'> {
  columnId: string
  rowId: string
  value: ICellValue
  oldValue?: ICellValue
}

export interface IUpdateTableViewCell extends IContext {
  cells: ITableViewCell[]
}

export interface IMapJoinColumn {
  sourceColumnId: string
  sourceColumnName?: string
  targetColumnId: string
}

export interface ITableJoin {
  publicId: string
  joinTableId: string
  joinViewId: string
  joinTableName: string
  joinViewName: string
  joinColumns: IMapJoinColumn[]
  dataColumns: IMapJoinColumn[]
  isOneToMany: boolean
}

export interface ITable {
  publicId: string
  projectPublicId: string
  projectName: string
  name: string
  permissionLevel: number
  columns: ITableColumn[]
  permissions: string[][]
  defaultViewId: string
  rows: ITableRow[]
  joins: ITableJoin[]
  isDeleted: boolean
  deletedAt: string | null
  createdAt: string
  updatedAt: string
  keepValidationsInSync: boolean
  keepColoursInSync: boolean
  isSynced: boolean
  syncProvider: string | null
  syncType: string | null
  isSyncing: boolean
  lastSync?: string
  syncHourlyFrequency: number
  allowComments: boolean
  failedSyncAttempts: number
  duplicatedChildren: IDuplicatedChild[]
  syncProjectIds?: string[]
  syncViewIds?: string[]
  syncAllHubs?: boolean
}

export interface ISimpleTable {
  publicId: string
  name: string
  logo: string | null
  type: string | null
  joins: ITableJoin[]
  isDeleted: boolean
  deletedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface ITableRow {
  sortOrder: number
  publicId: string
  rowData?: ICellValue[] // optional — populated for small tables and synthetic rows
  _rowIndex: number // stable index into CompressedColumnStore (-1 when not using store)
  // Set only in tree group view: the visible-ancestor depth used to indent the
  // tree-leaf cell so rows align under the deepest non-hidden group header above them.
  treeDepth?: number
  originalColumnId?: string // optional — the original column this row represents (for transposed views)
  // True when a row-lock rule keyed off a column hidden from the view locks this row. Such rules
  // are evaluated server-side (the hidden column's values, and which rule matched, are never sent
  // to the client — only this single flag) and streamed here so getRowLockState can apply them.
  hiddenRowLocked?: boolean
}

// `IJsonCellValue` is the value stored in a `json` column cell: a top-level JSON object.
// It is intentionally NOT a member of the `ICellValue` union: its string index signature
// would make every `ICellValue` property access (e.g. `value.toLowerCase()` in filtering)
// resolve to `unknown`. JSON cells narrow to this type at their own boundary instead.
export type IJsonCellValue = { [key: string]: unknown }
export type ICellValue = null | string | number | boolean | ISummaryUser | string[] | IFile[] | IVote[]

export type ITableRowCompressed = Array<unknown>

// One streamed column in the `columnar` rows-stream format: `[mode, payload]`. Mode 0 carries the
// values themselves; mode 1 carries all string values NUL-joined plus the null positions; modes 2
// and 4 carry little-endian uint16 / uint32 dictionary indices (0 = null) into the dictionary the
// client grows from `dictUpdates`; mode 6 means every cell in the batch is null and has no payload.
// See `helpers/columnar-stream.ts`.
export type IColumnarColumnPayload = [mode: number, payload: Uint8Array | unknown[] | [string, number[]] | null]

// The row data of one batch frame of the `columnar` rows-stream format (dictionary updates and
// locked-row indices travel beside it, as they do for the `rows` format).
export interface IColumnarBatch {
  rowCount: number
  // 16 raw bytes per row (uuid_send), in row order.
  publicIds: Uint8Array
  // Little-endian float64 per row, in row order.
  sortOrders: Uint8Array
  columns: IColumnarColumnPayload[]
}

// a cutdown version of the full project structure
export interface ISimpleProject {
  publicId: string
  name: string
}

export interface IProjectUser {
  createdAt: string
  favourite: boolean
  projectRole: string
  updatedAt: string
  user: Partial<ISummaryUser>
}

export interface IProjectView {
  name: string
  type: string
  tableId: string
  logo: string
  documentTable: ITable
}

export interface ILink {
  source: string
  target: string
}

export interface IHeadingStyle {
  publicId?: string
  level?: number
  colour: string
  bold: boolean
  italic: boolean
  underline: boolean
  fontSize: number
  numberingStyle?: number
  startAt0?: boolean
  alignment?: 'left' | 'center' | 'right'
}

export interface IProject {
  createdAt: string
  deletedAt: string | null
  isDeleted: boolean
  name: string
  primaryColour: string
  fontColour: string
  publicId: string
  updatedAt: string
  loading: boolean
  role: ProjectRoles
  defaultProcessId: string
  allowTableExport: boolean
  allowDocumentExport: boolean
  hubFolders: IHubFolder[]
  sidebarResources: ISidebarResource[]
  defaultBanner: string | null
  defaultHeaderBackgroundColor: string
  defaultHeaderTextColor: string
  processTitleFontSize: number
  processTitleBold: boolean
  processTitleItalic: boolean
  processTitleUnderline: boolean
  processTitleColour: string
  processTitleAlignment: 'left' | 'center' | 'right'
  headingStyles: Array<IHeadingStyle>
  public: boolean
  defaultDateFormat: string
  defaultDatetimeFormat: string
  domainsAccess: string[]
  mfaRequired: boolean
  hideProcessCreated: boolean
  disablePersonalViewSaving: boolean
  duplicatedChildren: IDuplicatedChild[]
  wordTemplate: string | null
  wordTableStyle?: string | null
  wordTableStyles?: string[] | null
  site: ISite | null
  canConfigureSync?: boolean
  canChangeSite?: boolean
}

export interface IUserToIntegration {
  provider: string
  accessToken: string
  refreshToken: string
  expiresAt: string
  region: string
}

export interface IUserObject {
  publicId: string
  firebaseUserId: string
  name: string
  email: string
  profilePicture: string
  integrations: IUserToIntegration[]
  authToken?: string
  authTokenExpiresAt?: string
  apiKeys: IApiKey[]
  bio: string
  linkedin: string
  phone: string
  twitter: string
  university: string
  organisation: string
  location: string
  website: string
  universityDegree: string
  projectsWorkedOn: string[]
  constructionSoftware: string[]
  specialisms: string[]
  company: string
  isSuperAdmin?: boolean
  isActive?: boolean
  isEmailVerified?: boolean
  isRateLimited?: boolean
  createdAt?: string
  favourites: Partial<IProject>[]
  is2FaEnabled?: boolean
  hasPassword?: boolean
  invitedBy?: string
  invitesSent?: number
  projectsOn?: number
  subscriptionLevel: number
  userLevelSubscription: number
  eligibleForFreeTrial: boolean
  isOnFreeTrial: boolean
  freeTrialDaysRemaining: number
  allowSupportAccess: boolean
  allowSupportAccessExpiresAt: string | null
  optOutWelcomeEmail?: boolean
  optOutSyncEmail?: boolean
  optOutAiEmail?: boolean
  optOutHubEmail?: boolean
  optOutDuplicationEmail?: boolean
  optOutDeprecationEmail?: boolean
  optOutMentionEmail?: boolean
  optOutAssignmentEmail?: boolean
  optOutMarketingEmail?: boolean
  optOutCommunityEmail?: boolean
  /** Light / Dark / System. Absent on an older cached user object. */
  theme?: ThemePreference
  adminOrganisations?: IOrganisation[]
  isDeleted: boolean
  isTeamManager: boolean
  managedTeams: { publicId: string; name: string; orgId: string }[]
  team: {
    orgName: string
    orgPublicId: string
    teamName: string
    teamPublicId: string
    managerNames: string[]
    orgPlanTier: 'professional' | 'team' | 'scale_up'
  } | null
  managedSeatCount?: number | null
}

export interface IAchievementsObject {
  createdProcesses?: number
  createdTables?: number
}

export interface IPinnedProjectObject {
  name: string
  publicId: string
  primaryColour: string
  fontColour: string
  contributors: number
  views: number
}

export interface ILogObject {
  change?: string
  changeId?: string
  createdAt: string
  resource: string
  publicId: string
  context: any
  user: {
    firebaseUserId: string
    name: string
    email: string
    profilePicture: string
    kind: string
  }
  verb: string
  contextProject: Pick<IProject, 'publicId' | 'name'> | null
  contextProcess: Pick<IProcess, 'publicId' | 'name'> | null
  contextProcessSection: Pick<IProcessSectionObject, 'publicId' | 'name'> | null
  contextProcessResponse: { publicId: string } | null
  contextTable: Pick<ITable, 'publicId' | 'name' | 'defaultViewId'> | null
  contextTableView: Pick<ITableView, 'publicId' | 'name' | 'tablePublicId'> | null
  contextTableColumn: Pick<ITableView, 'publicId' | 'name'> | null
  channel?: string
}

export interface IEmbedSubmission {
  publicId: string
  enableSubmission: boolean
  resetAfterSubmission: boolean
  submittedAt: string | null
  submittedBy: IUserObject | null
  createdAt: string
  updatedAt: string
}

export interface IProcessSectionObject {
  publicId: string
  position: number
  description: any
  name: string
  isDeleted: boolean
  deletedAt: string
  pdfIncludeDescription: boolean
  pdfIncludeSection: boolean
  pageBreakBefore: boolean
  children: Array<IProcessSectionObject>
  submissions?: Array<IEmbedSubmission>
  status: number
  commentsThread: ICommentThreadObject[]
  openCommentThreads: number
  parentId: string
  createdAt: string
  expanded?: boolean
  visible?: boolean
}

export interface IDuplicatedChild {
  publicId: string
  link: string | null
  name: string
  createdAt: string
  user: string | null
  deletedAt: string | null
  deletedBy: string | null
}

export interface IProcessObject {
  publicId: string
  projectPublicId: string
  projectName: string
  description: any
  logo: string | null
  name: string
  createdAt: string
  updatedAt: string
  isDeleted: boolean
  deletedAt: string
  children: Array<IProcessSectionObject>
  permissionLevel: number
  variables: Array<string>
  variableValues: Array<string>
  permissions: IAccessPolicy[]
  isTemplate: boolean
  lockedTemplate: boolean
  expandByDefault: boolean
  defaultFormat: string
  defaultOrientation: string
  allowComments: boolean
  createdBy?: ISummaryUser
  duplicatedChildren?: IDuplicatedChild[]
  templateRender?: boolean
  bannerHeight?: number | null
}

export interface IProcess {
  publicId: string
  name: string
  createdAt: string
  projectPublicId: string
  projectName: string
}

export interface IProjectSecret {
  publicId: string
  name: string
  value: string
  isReadOnly: boolean
  createdAt: string
  updatedAt: string
}

export interface ICell {
  id: string
  value: string
  column: {
    name: string
    publicId: string
  }
}

export interface ITableWithCells {
  name: string
  publicId: string
  cells: ICell[]
}

export interface IApiKey {
  hash: string
  name: string
  prefix: string
  publicId: string
  accessLevel: number
  siteRestrictions: string[]
  projectRestrictions: string[]
  tableRestrictions: string[]
  documentRestrictions: string[]
}

export interface IFileParentResource {
  resource: 'process' | 'document_table' | 'project' | 'user'
  publicId: string
}

export interface IVote {
  firebaseUserId: string
  type: boolean
  timestamp: string
}

export interface IFile {
  publicId: string
  filename: string
  filesize: number
  extension: string
  mimetype: string
  url: string
  createdAt: string
  version: number
}

export interface ISignedUrlToken {
  expires: number
  token: string
  url: string
  filename: string
}

export interface AccessLevelInterface {
  level: string
  visible: boolean
  anchorEl: any
  tag: string
}

export interface ITableViewSort {
  columnId: string
  columnName: string
  direction: sortDirections
}

export const STACKED_CHART_TYPES = ['stacked-bar', 'stacked-horizontal-bar']
export const PROPORTIONAL_CHART_TYPES = ['pie', 'doughnut', 'polar-area']
export const CLUSTERED_CHART_TYPES = ['clustered-bar', 'clustered-horizontal-bar']
export const CHARTS_WITH_GROUPING = [...STACKED_CHART_TYPES, ...CLUSTERED_CHART_TYPES]

export type IChartType =
  | null
  | 'bar'
  | 'horizontal-bar'
  | 'stacked-bar'
  | 'stacked-horizontal-bar'
  | 'clustered-bar'
  | 'clustered-horizontal-bar'
  | 'grouped-bar'
  | 'area'
  | 'line'
  | 'multiaxis-line'
  | 's-curve'
  | 'pie'
  | 'doughnut'
  | 'polar-area'
  | 'radar'
  | 'scatter'
  | 'bubble'
  | 'gantt'
  | 'card'

export type IChartAggregate = null | 'count' | 'sum' | 'avg' | 'earliest' | 'latest'
export type IChartSort = null | 'asc' | 'desc'
export interface ICalendarSettings {
  labelColumnPublicId: string | null
  startColumnPublicId: string | null
  endColumnPublicId: string | null
}

export interface ITableViewChart {
  columnLabelId: string | null
  columnValueIds: string[]
  columnStackId: string | null

  // Gantt specific
  columnGanttStartDateId: string | null
  columnGanttEndDateId: string | null
  columnGanttDeadlineId: string | null
  chartType: IChartType
  showLegend: boolean
  showValues: boolean
  showPercentage: boolean
  // Prints the view name as a heading above the plot (every chart type).
  showTitle: boolean
  // Card charts only: the label inside each tile (value column name, or "group value + column name").
  showCardLabel: boolean
  aggregate: IChartAggregate
  sortAggregate: IChartSort
  maxCategories: number | null
}

export interface ITableViewFilter {
  columnId: string
  columnName: string
  filterType: IFilterType
  orGroup: string
  value: string
  multipleValues: string[]
  publicId?: string
}

export interface ITableViewColour {
  columnId: string
  columnName: string
  filterType: IFilterType
  value: string
  multipleValues: string[]
  backgroundColour: string
  fontColour: string
  fontBold: boolean
  fontItalic: boolean
  fontUnderline: boolean
  applyToEntireRow: boolean
}

export interface ITableViewRowLock {
  columnId: string
  columnName: string
  filterType: IFilterType
  // `value` is omitted when `filterType === 'all_values'` and when the rule
  // uses `multipleValues` (one_of / not_one_of). `multipleValues` is omitted
  // for every other filter type. The RowLockSchema serializer on the backend
  // strips whichever field doesn't apply (see api/app/schema/resources.py
  // RowLockSchema.remove_properties), so the type mirrors what arrives over
  // the wire.
  value?: string
  multipleValues?: string[]
  applyToEntireRow: boolean
  publicId?: string
}

export interface IRowLevelColour {
  backgroundColour: string
  fontColour: string
  fontBold: boolean
  fontItalic: boolean
  fontUnderline: boolean
  matchedRuleIndex: number // Index of the rule that matched, or -1 if no match
}

export interface ITableViewHiddenColumn {
  columnId: string
  columnName: string
}

export interface ITableViewGroup {
  columnId: string
  columnName: string
  direction: sortDirections
}

/** Shared width slots for any pivot-style view (transpose, matrix, …).
 *
 * The wire shape is identical across view types — `field_column_width` + a
 * `data_column_widths` map. The map's *keys* mean different things per view
 * (transpose: source row publicId; matrix: synthetic column ID), so don't
 * try to share widths across view types.
 */
export interface IPivotColumnWidths {
  fieldColumnWidth: number | null
  dataColumnWidths: Record<string, number> | null
}

export interface ITransposeSettings extends IPivotColumnWidths {
  pivotColumnId: string | null
}

export interface IMatrixSettings extends IPivotColumnWidths {
  pivotColumnId: string | null
  rowLabelColumnIds: string[]
  dataFieldColumnIds: string[]
}

/** A saved Custom View page. Only the single-view response carries its `html`; view lists omit it. */
export interface ICustomView {
  runtimeVersion: 1
  updatedAt: string
  /** Absent until fetched; null when the server can't find the page's HTML. */
  html?: string | null
}

/** A Custom View's unpublished page, from MortaAI: only table owners see it, until one saves the view. */
export interface ICustomViewDraft {
  /** The draft's ID, sent back to publish it, so nobody publishes a draft they haven't seen. */
  page: string
  updatedAt: string
  html?: string | null
}

export interface ITableView {
  customView?: ICustomView | null
  /** Owners only: undefined until fetched, null when there's no draft. */
  customViewDraft?: ICustomViewDraft | null
  /** Sent with a save to publish the draft as the view's page. */
  publishCustomViewDraft?: string
  publicId: string
  name: string
  default: boolean
  type: number
  description: EditorContent | null
  filterSettings: ITableViewFilter[]
  groupSettings: ITableViewGroup[]
  colourSettings: ITableViewColour[]
  rowLockSettings: ITableViewRowLock[]
  hiddenColumns: ITableViewHiddenColumn[]
  sortSettings: ITableViewSort[]
  chartSettings: ITableViewChart | null
  transposeSettings: ITransposeSettings | null
  matrixSettings: IMatrixSettings | null
  calendarSettings: ICalendarSettings | null
  permissionLevel: number
  frozenIndex: number
  isDefault: boolean
  rowHeight: number
  disableNewRow: boolean
  disableSyncCsv: boolean
  allowContributorDelete: boolean
  displayValidationErrorRows: number
  displayCommentRows: number
  collapsedGroupView: boolean
  unpackMultiselectGroupView: boolean
  treeGroupView: boolean
  tablePublicId?: string
  isPersonalView: boolean
  configurationParentView?: {
    publicId: string
    name: string
  } | null
  personalViewOwner?: ISummaryUser | null
  createdAt?: string
}

export interface ITableViewWithTable extends ITableView {
  documentTable: ISimpleTable
}

export interface ITableViewWithColumns extends ITableView {
  columns: ITableViewColumn[]
  columnsHydrated?: boolean
  documentTable?: ISimpleTable
}

export interface ITableColumnStat {
  name: string
  publicId: string
  aggregation: {
    name: string
    value: number
  }
}

export interface ISummaryUser {
  publicId: string
  name: string
  firebaseUserId: string
  email: string
  profilePicture: string
  kind: string
  isServiceAccount?: boolean
  tags?: ITag[]
}

export interface ICommentThreadObject {
  publicId: string
  comments: ICommentObject[]
  resolvedAt: Date
  createdAt: Date
  updatedAt: Date
  deletedAt: Date
  resolver: ISummaryUser
  isCommentInitiator: boolean
}

export interface ICommentObject {
  publicId: string
  // Markdown body (mentions as @[Name](mention:uuid) tokens); legacy comments hold plain text.
  commentText: string
  user: ISummaryUser
  isOwner: boolean
  resolvedAt: Date
  createdAt: Date
  updatedAt: Date
  deletedAt: Date
}

export interface ICommentThreadStats {
  referenceId: string
  openCommentThreads: number
  resolvedCommentThreads: number
}

export interface IAccessAttribute {
  publicId: string
  kind: AccessAttributeKinds
  user?: Partial<IUserObject>
  project?: ISimpleProject
  tag?: ITag
  documentTable?: Partial<ITable>
}

export interface IAccessResource {
  publicId: string
  kind: AccessResourceKinds
  process?: IProcess
  documentTable?: ITable
}

export interface IAccessPolicy {
  publicId: string
  role: number
  roleLabel: string
  accessAttribute: IAccessAttribute
  accessResource: IAccessResource
}

export interface ITag {
  publicId: string
  referencePublicId: string
  value: string
  projectPublicId: string
}

// a ProjectList model on the backend
export interface IProjectMember {
  role: ProjectRoles
  roleAssignedMethod: 'direct' | 'organisation' | 'site'
  favourite: boolean
  user: ISummaryUser
  createdAt: string
}

export interface EditorContent {
  content: RawDraftContentState
}

export interface UserPublicModel {
  bio: string
  company: string
  companyRole: string
  phone: string
  email: string
  name: string
  profilePicture: string
  projects: Array<string>
  twitter: string
  linkedin: string
  projectsWorkedOn: Array<any>
  university: string
  universityDegree: string
  constructionSoftware: Array<string>
  specialisms: Array<string>
}

export interface IContextProperties {
  projectId: string
  processPublicId?: string
  processSectionPublicId?: string
}

export interface IContext {
  context: IContextProperties
}

export interface IOAuthConnectUrl {
  url: string
}

export interface IMemberInvite {
  publicId: string
  email: string
  createdAt: string
  invitedBy: {
    firebaseUserId: string
    name: string
    email: string
    profilePicture: string
    kind: string
  }
  tags: ITag[]
  projectRole: string
}

export interface IColumnAlterOptions {
  runScriptOnAllCells?: boolean
  dateConversionFormat?: AllowedDateFormats | AllowedDatetimeFormats
}

export interface I2FaSetup {
  secret: string
  url: string
}

export interface ITableViewJoin {
  joinViewId: string
  joins: [{ currentViewColumn: string; externalViewColum?: string }]
  columns: string[]
}

export interface ICeleryQueue {
  name: string
  pending: number
  active: number
  reserved: number
}

export interface ICeleryStats {
  queues: Record<string, any>[]
  workers: Record<string, any>[]
}

export interface IVariable {
  value: string
  table: string
  column: string
  id: string
  colour?: string
}

export interface IProcessUsed {
  name: string
  link: string
}

export interface IViewUsed {
  processes: IProcessUsed[]
}

export interface IUsed {
  name: string
  link: string
  type: 'process' | 'sourceJoin' | 'targetJoin' | 'targetSelect' | 'sourceSelect'
}

/** A folder in a hub's sidebar tree. `parentId` is null for a top-level folder. */
export interface IHubFolder {
  publicId: string
  name: string
  parentId: string | null
  position: number
  /** ISO date-time. Absent from a backend older than the sidebar's Date created sort. */
  createdAt?: string
}

export type SidebarResourceKind = 'document' | 'table'

/** A live document or table as the sidebar lists it. `folderId` is null at the hub's top level. */
export interface ISidebarResource {
  publicId: string
  kind: SidebarResourceKind
  name: string
  folderId: string | null
  position: number
  defaultViewId: string | null
  /** This viewer may move it between folders: hub Owners always, anyone else when they own it. */
  canMove: boolean
  /** ISO date-time. Absent from a backend older than the sidebar's Date created sort. */
  createdAt?: string
}

/** One folder, document or table in a move request. */
export interface ISidebarMoveItem {
  kind: 'folder' | SidebarResourceKind
  id: string
}

export interface ITableFolder {
  name: string
  views: ITableViewWithTable[]
}

export interface IFolderContents {
  type: 'process' | 'table' | 'tables'
  tables?: ITableFolder[]
  process?: IProcessObject
  table?: ITable
  name: string
}

export interface IContextMenuState {
  open: boolean
  top: string
  right: string
  bottom: string
  left: string
  columnNumber?: number
  columnId?: string
  columnPanel?: 'formula' | 'script'
  rowNumber?: number
  rowId?: string
  defaultFilter?: ITableViewFilter
  defaultColour?: ITableViewColour
  groupKey?: string
}

export type CommentThreadStatusType = 'resolve' | 'reopen'
export type CommentReferenceType = 'process_section' | 'table' | 'table_view'
export type ActionMenuType = 'edit' | 'delete' | null

export interface OptionProps {
  label: string
  value?: string
  group?: string
  disabled?: boolean
  tooltip?: string
  // Rendered before the label, in the dropdown and on the closed trigger. Used where the options are a
  // kind of thing with a glyph of its own, such as a column type.
  icon?: ReactNode
}

export interface IResource {
  label: string
  link: string
}

export interface IRowsUpdatedSocket {
  rowPublicIds: string[]
}

export interface IResourcesUpdatedSocket {
  projectPublicId: string
  /** The tab that made the change, when it has already applied it (helpers/realtimeOrigin). */
  origin?: string
  /** The folders whose direct children changed (null = the top level); absent means anything may have. */
  containers?: (string | null)[]
}

/** POST /hub/<id>/sidebar/containers: the requested containers' direct children, as the hub GET lists them. */
export interface ISidebarContainers {
  hubFolders: IHubFolder[]
  sidebarResources: ISidebarResource[]
  /** Requested folders that no longer exist (or that this viewer can't see). */
  removed: string[]
}

export interface ISyncResource {
  name: string
  id: string
  group?: string
  region?: string
  properties?: string[]
  disabled?: boolean
}

export interface IIntegrationCatalogueField {
  key: string
  label: string
  required: boolean
  type: 'select' | 'multiselect'
  dependsOn: string | null
  sortOrder: number
  drilldown: boolean
  visibleForTypes: string[] | null
  staticOptions: { id: string; name: string }[] | null
  default: string | null
}

export interface FieldOption {
  id: string
  name: string
  group?: string
  disabled?: boolean
  parentId?: string
}

export interface IntegrationFieldSelectorProps {
  provider: string
  fields: IIntegrationCatalogueField[]
  selectedValues: Record<string, string[]>
  onFieldChange: (fieldKey: string, values: string[]) => void
  type: string
  // TODO: unify the import process from inside a table with the create from import
  compact?: boolean
}

export interface IIntegrationRedirects {
  [provider: string]: string | null
}

export interface IApiKeyFieldConfig {
  key: string
  label: string
  isSecret: boolean
  placeholder: string
}

export interface IApiKeyConfig {
  fields: IApiKeyFieldConfig[]
}

export interface IIntegrationCatalogueItem {
  display: string
  icon: string
  types: string[]
  authType: 'oauth' | 'api_key' | 'none'
  apiKeyConfig?: IApiKeyConfig
  fields?: IIntegrationCatalogueField[]
  disabledTypes?: string[]
}

export interface IIntegrationCatalogue {
  [provider: string]: IIntegrationCatalogueItem
}

export interface IntegrationButtonProps {
  enterprise?: string
  hub?: string
  project?: string
  topFolder?: string
  folder?: string
  company?: string
  license?: string
  projects?: string[]
  views?: string[]
  docTypes?: string[]
  formTypes?: string[]
  syncAllHubs?: boolean
}

export interface ChartData {
  label: string
  value: number
  color: string
}

export interface ChartProps {
  type: IChartType
  data: ChartData[]
  showLegend: boolean
  showValues: boolean
  loading: boolean
  axisNames: { x: string; y: string }
}

export type BarChartProps = Omit<ChartProps, 'type' | 'loading'>
export type LineChartProps = Omit<ChartProps, 'type' | 'loading'>
export type PieChartProps = Omit<ChartProps, 'type' | 'loading' | 'axisNames'>

export interface IIntegrationPassthroughResponse {
  status: string
  contentType: string
  body: Record<string, any>
  headers: Record<string, string>
}

export type IIntegrationSyncResourceTypes = IntegrationSyncResourceTypes

export enum IAutodeskProjectType {
  BIM360 = 'BIM360',
  ACC = 'ACC'
}

export interface IAutodeskProject {
  id: string
  attributes: { name: string; extension: { data: { projectType: string } } }
}
export interface IIntegrationData {
  syncFields?: Record<string, string | string[] | undefined>
  syncAllHubs?: boolean
  type?: IIntegrationSyncResourceTypes
}

export interface IColourItem {
  colour: ITableViewColour
  index?: number
}

export interface ISubscriptionComparison {
  path: string
  endpoint: string
  method: string
  description: string
  free: boolean
  paid: boolean
}

export interface ISnackbarObject {
  message?: string
  status?: string
  action?: any
  duration?: number
  path?: string
  method?: string
  /**
   * Offer AI Support (and the request details) with an error. Defaults to offering it only when
   * there is no message to show, since a message set by the app says what went wrong.
   */
  offerSupport?: boolean
}

export interface IFormulaDiagnostic {
  message: string
  start: number
  end: number
  line: number
  column: number
}

export interface IFormulaValidation {
  valid: boolean
  diagnostic: IFormulaDiagnostic | null
}

export interface IFormulaHelper {
  category: string
  name: string
  description: string
  examples: string[]
  syntax: string
  useOnOwn: boolean
  canonicalName?: string
  aliases?: string[]
  args?: number
  minArgs?: number
  maxArgs?: number
  argumentKinds?: string[]
}

export interface IOrganisationUser {
  user: {
    name: string
    email: string
    createdAt: string
    publicId: string
    firebaseUserId: string
    profilePicture?: string
    subscriptionLevel: number
    userLevelSubscription: number
    isOnFreeTrial: boolean
    lastSignedIn?: string
    organisationTeamId?: string | null
    organisationTeamName?: string | null
  }
  sites: { publicId: string; name: string }[]
  role: string
  isExternal?: boolean
  isPending?: boolean
}

export interface IOrganisationHub {
  publicId: string
  name: string
  createdAt: string
  public: boolean
  domainsAccess: string[]
  mfaRequired: boolean
  site: Pick<ISite, 'publicId' | 'name'> | null
}

export interface ISiteUserRow {
  email: string
  role: OrganisationRoles
}

export interface IUserToSite {
  role: OrganisationRoles
  createdAt: string
  updatedAt: string
  user: ISummaryUser
}

export interface ISite {
  publicId?: string
  name: string
  createdAt?: string
  updatedAt?: string
  users: ISiteUserRow[] | IUserToSite[]
  organisation?: {
    publicId: string
    name: string
  }
  adminCount?: number
  hubCount?: number
  teamId?: string | null
  teamName?: string | null
  canCreateHub?: boolean
}

export interface ISitesModalOrganisation {
  publicId: string
  sites: ISite[]
  users: IOrganisationUser[]
  teams: IOrganisationTeam[]
}

export interface IOrganisation {
  publicId: string
  createdAt: string
  updatedAt: string
  name: string
  enforceLogin: string | null
  users: IOrganisationUser[]
  hubs: IOrganisationHub[]
  sites: ISite[]
  domains: string[]
  isSamlConfigured: boolean
  idpSsoUrl: string | null
  idpIssuer: string | null
  idpCertificate: string | null
  enableUserApiKeys: boolean
  disablePersonalViewSaving: boolean
  restrictHubCreation: boolean
  restrictSyncConfiguration: boolean
  paidUserSeats: number
  assignedPaidUsers: number
  planTier: 'professional' | 'team' | 'scale_up'
  multiYearContract: boolean
  teams: IOrganisationTeam[]
  serviceAccount?: { publicId: string; name: string; email: string } | null
}

export interface ITeamManager {
  publicId: string
  name: string
  email: string
}

export interface ITeamMember {
  publicId: string
  name: string
  email: string
  createdAt: string
  lastLoginAt: string | null
  profilePicture: string | null
  firebaseUserId: string | null
  isExternal?: boolean
}

export interface IPendingMember {
  email: string
  createdAt: string
}

export interface IOrganisationTeam {
  publicId: string
  name: string
  seatLimit: number
  seatsUsed: number
  siteCount: number
  pendingCount: number
  isDefault: boolean
  managers: ITeamManager[]
  createdAt: string
  updatedAt: string
}

export interface ITeamHub {
  publicId: string
  name: string
  createdAt: string
  public: boolean
  site: { publicId: string; name: string } | null
}

export interface IOrganisationTeamDetail extends IOrganisationTeam {
  members: ITeamMember[]
  pendingMembers: IPendingMember[]
  seatsAvailable: number
  hubs: ITeamHub[]
}

export interface ITeamsListResponse {
  teams: IOrganisationTeam[]
  totalSeats: number
  allocatedSeats: number
  defaultSeats: number
}

export interface IUserSession {
  publicId: string
  createdAt: string
  expiresAt: string
  ipAddress?: string
  userAgent?: string
  deviceType?: string
  platform?: string
}

export interface IBlockingQuery {
  pid: number
  query: string
}

export interface IStuckLock {
  pid: number
  usename: string
  blockedQuery: string
  blocking: IBlockingQuery[]
}

export interface IAgentStreamEvent {
  type: 'thinking' | 'content' | 'done' | 'error' | 'notice' | 'context' | 'status' | 'action'
  content?: string
  conversationId?: string
  messageId?: string
  data?: Record<string, any>
  // On `done`: the chat carries on by itself once every card from this reply is accepted.
  resumeAfterActions?: boolean
}

export interface IScriptInventoryRow {
  tablePublicId: string
  tableName: string
  columnPublicId: string
  columnName: string
  scriptUpdatedAt: string
  viewPublicId: string | null
}

// ── Dependency graph (the hub's Relationships page) ─────────────────────
export type DependencyKind = 'formula' | 'lookup' | 'select' | 'live_select' | 'document'

export interface IDependencyNodeRef {
  kind: 'table' | 'document' | 'column' | 'view' | 'section'
  publicId: string
  name?: string
  container?: IDependencyNodeRef
  defaultViewId?: string // tables only: the view a table opens at
}

export interface IDependencyOverviewNode {
  kind: 'table' | 'document'
  publicId: string
  name: string
  columns?: number
  defaultViewId?: string
  /** Tables only: per kind, how many of its columns read another column of the same table. Those
   *  have no arrow, but a table with them is not unconnected. */
  internal?: Record<DependencyKind, number>
}

export interface IDependencyOverview {
  nodes: IDependencyOverviewNode[]
  edges: { source: IDependencyNodeRef; target: IDependencyNodeRef; counts: Record<DependencyKind, number> }[]
  populated: boolean
}

export interface IDependencyMember {
  kind: 'column' | 'section'
  publicId: string
  name: string
  detail: string
}

export interface IDependencyExpansion {
  node: IDependencyNodeRef
  members: IDependencyMember[]
  edges: { source: IDependencyNodeRef; target: IDependencyNodeRef; kind: DependencyKind; role: string }[]
}

export type WhatsNewTag = 'new_feature' | 'improvement' | 'announcement'

export interface IWhatsNewEntry {
  issue: number
  prs: number[]
  tag: WhatsNewTag
  title: string
  body: string
  link?: string
}

export interface IWhatsNewExcludedStory {
  issue: number
  title: string
  reason: string
}

export interface IWhatsNewRelease {
  id: string
  date: string
  entries: IWhatsNewEntry[]
  // Stories the drafting script judged not user facing, or a reviewer removed. Not rendered.
  excluded?: IWhatsNewExcludedStory[]
}

export interface IWhatsNewData {
  releases: IWhatsNewRelease[]
}

export interface IUserNotificationData {
  title?: string
  preview?: string | null
  url?: string | null
  resourceName?: string | null
  columnName?: string | null
}

export interface IUserNotification {
  publicId: string
  notificationType: string
  referenceType: string
  data: IUserNotificationData | null
  actor: Partial<ISummaryUser> | null
  project: Pick<IProject, 'publicId' | 'name'> | null
  readAt: string | null
  dismissedAt: string | null
  createdAt: string
}

export interface IUserNotificationListMetadata {
  total: number
  size: number
  nextPageToken: string | null
  unreadCount: number
}

export type EmailPreferences = Partial<
  Pick<
    IUserObject,
    | 'optOutWelcomeEmail'
    | 'optOutSyncEmail'
    | 'optOutAiEmail'
    | 'optOutHubEmail'
    | 'optOutDuplicationEmail'
    | 'optOutDeprecationEmail'
    | 'optOutMentionEmail'
    | 'optOutAssignmentEmail'
    | 'optOutMarketingEmail'
    | 'optOutCommunityEmail'
  >
>
